<template>
	<div
		ref="block"
		:data-block="block.constructor.name"
		v-bind="dataset"
		:class="[{ 'show-background': showBg || isSettingsOpen }, ...block.classes]"
		@mouseenter="showBackground"
		@mouseleave="!focused && hideBackground()"
		@click="focusHandler"
		v-click-outside="clickOutsideHandler"
	>
		<component
			ref="view"
			:is="block.constructor.viewComponent"
			:settings="block.settings"
			:value="block"
			@focus="focusHandler"
		/>

		<portal :to="block.id">
			<sp-provider theme="dark">
				<div
					ref="background"
					:style="backgroundStyle"
					v-show="isBackgroundVisible"
					class="block-background"
					:class="{ focused, 'unknown-block': block.constructor.name === 'Unknown' }"
				>
				</div>
	
				<div
					ref="controlButtons"	
					class="control"
					v-show="isControlButtonsVisible"
				>
					<sp-quick-actions open>
						<sp-action-button
							@click="moveBlockDown"
							:disabled="disableMoveDown"
						>
							<sp-icon :icon="$options.components.ChevronDown" />
						</sp-action-button>
	
						<sp-action-button
							@click="moveBlockUp"
							:disabled="disableMoveUp"
						>
							<sp-icon :icon="$options.components.ChevronUp" />
						</sp-action-button>
	
						<sp-action-button
							@click="removeBlock"
						>
							<sp-icon :icon="$options.components.Delete" />
						</sp-action-button>
	
						<sp-action-button
							:selected="isSettingsOpen"
							:disabled="!block.constructor.settingsComponent"
							ref="settingsButton"
							@click="toggleSettings"
						>
							<sp-icon :icon="$options.components.MoreVertical" />
						</sp-action-button>
					</sp-quick-actions>
				</div>
	
				<transition name="fade">
					<sp-popover ref="settings" :open="isSettingsOpen || separatedPopover">
						<sp-dialog
							dismissable
							@update:open="isSettingsOpen = false"
							size="S"
							style="--spectrum-dialog-confirm-padding: 16px; width: 270px;"
						>
							<sp-heading>{{block.constructor.name.toUpperCase() }} SETTINGS</sp-heading>
							<sp-divider></sp-divider>
							<sp-content>
								<sp-text class="option-label" style="margin-top: 0;">CLASS OPTIONS</sp-text>
								<template v-for="(humanName, className) in block.constructor.classOptions">
									<template v-if="isObject(humanName)">
										<sp-radio-group
											vertical
											v-model="block.classOptions[className]"
											:key="className"
											:label="capitalized(className)"
										>
											<sp-radio value="">None</sp-radio>
											<sp-radio
												v-for="(humName, clsName) in humanName"
												:value="clsName"
												:key="clsName">
												{{humName}}
											</sp-radio>
										</sp-radio-group>
									</template>
									<template v-else>
										<sp-switch
											v-model="block.classOptions[className]"
											:key="className"
										>
											{{ humanName }}
										</sp-switch>
									</template>
								</template>
	
								<div style="margin-top: 20px;"></div>
								<sp-divider size="S" />
								<sp-text class="option-label">BLOCK OPTIONS</sp-text>
								<component :is="block.constructor.settingsComponent" :settings="block.constructor.settings" :value="block" />
	
								<div style="margin-top: 2em"></div>
								<sp-action-group justified>
									<sp-action-button @click="duplicate">
										<sp-icon :icon="$options.components.Copy" />
										Duplicate
									</sp-action-button>
									<sp-action-button @click="immediatelyRemoveBlock">
										<sp-icon :icon="$options.components.Delete" />
										Delete
									</sp-action-button>
								</sp-action-group>
							</sp-content>
						</sp-dialog>
					</sp-popover>
				</transition>
			</sp-provider>
		</portal>
	</div>
</template>

<script>
import CssGrid from "./CssGrid"
import OnEventOutside from "./OnEventOutside"
import Util from "../scripts/Util"
import ClickOutside from "../directives/ClickOutside"

import {
	SpProvider,
	SpQuickActions,
	SpActionGroup,
	SpActionButton,
	SpIcon,
	SpPopoverTrigger,
	SpDialog,
	SpPopover,
	SpHeading,
	SpText,
	SpDivider,
	SpContent,
	SpSwitch,
	SpRadioGroup,
	SpRadio,
} from 'vue-spectrum'

import ChevronDown from 'vue-spectrum-workflow-icons/dist/ChevronDown'
import ChevronUp from 'vue-spectrum-workflow-icons/dist/ChevronUp'
import MoreVertical from 'vue-spectrum-workflow-icons/dist/MoreVertical'
import Copy from "vue-spectrum-workflow-icons/dist/Copy"
import Delete from "vue-spectrum-workflow-icons/dist/Delete"

import { Portal } from 'portal-vue'

import * as Popper from "@popperjs/core";

export default {
	name: "Block",
	components: {
		OnEventOutside,
		Portal,
		CssGrid,
		SpProvider,
		SpQuickActions,
		SpActionGroup,
		SpActionButton,
		SpIcon,
		SpPopoverTrigger,
		SpDialog,
		SpPopover,
		SpHeading,
		SpText,
		SpDivider,
		SpContent,
		SpSwitch,
		SpRadioGroup,
		SpRadio,
		ChevronDown,
		ChevronUp,
		Copy,
		Delete,
		MoreVertical,
	},
	directives: {
		ClickOutside
	},
	provide() {
		return {
			nextEditableInView: this.nextEditableInView
		}
	},
	inject: [
		'slottedBlocks',
		'appSettings',
		'nextBlockComponent',
		'showControl',
		'hideControl',
	],
	model: {
		prop: 'block',
	},
	props: {
		block: {
			type: Object,
		},
		disableMoveUp: {
			type: Boolean,
			default: false
		},
		disableMoveDown: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			showBg: false,
			isSettingsOpen: false,
			separatedPopover: false,
			startDragPos: { x: 0, y: 0 },
			popperPos: { x: 0, y: 0 },
			popperModifiers: [],
			isObjectEmpty: Util.isObjectEmpty,
			settingsPopper: null,
			focused: false,
			backgroundStyle: {},
			backgroundVisible: false,
		}
	},
	mounted () {
		const _this = this;
		this.$once("hook:beforeDestroy", () => {
			_this.settingsPopper && _this.settingsPopper.destroy();
		})
		
		const resizeObserver = new ResizeObserver(entries => {
			if (_this.isBackgroundVisible) {
				entries.forEach(entry => {
					const { width, height  } = entry.contentRect

					Object.assign(_this.$refs.background.style, {
						width: width+20+'px',
						height: height+20+'px'
					})

					_this.$refs.background.style.removeProperty('transform')

					const { diffX: x, diffY: y } = Util.matchTransformMatrix({
						a: _this.$refs.background,
						b: _this.$el
					})

					Object.assign(_this.$refs.background.style, {
						transform: Util.transformStyle({ x: x - 10, y: y - 10 })
					})
				})
			}
		})
		resizeObserver.observe(this.$el)
	},
	computed: {
		dataset () {
			let obj = this.block.dataset
			let dataset = {};

			Object.keys(obj).forEach(datakey => {
				dataset["data-" + Util.toKebabCase(datakey)] = obj[datakey]
			});

			return dataset;
		},
		isBackgroundVisible () {
			return this.focused || this.backgroundVisible
		},
		isControlButtonsVisible () {
			return this.focused
				|| (this.isSettingsOpen && !this.separatedPopover)
		}
	},
	watch: {
		isControlButtonsVisible () {
			if (this.isControlButtonsVisible) {
				this.showControlButtons()
			} else {
				this.hideBackground()
			}

			if (!this.isControlButtonsVisible && this.showRemove) {
				this.showRemove = false
			}
		},
		"block.classOptions": {
			immediate: true,
			deep: true,
			handler () {
				if (Util.isObjectEmpty(this.block.constructor.classOptions)) return;
				this.block.classes = [];


				for (let [className, val] of Object.entries(this.block.classOptions)) {
					if (typeof val === "string" &&  val !== "") {
						this.block.classes.push(className)
						this.block.classes.push(val)
					} else if (typeof val === "boolean" && val) {
						this.block.classes.push(className)
					}
				}
			},
		},
	},
	methods: {
		duplicate () {
			this.$emit('duplicate', this.block);
		},
		moveBlockDown () {
			this.focused = false
			this.isSettingsOpen = false
			this.$emit('move-block-down', this.block)
		},
		moveBlockUp () {
			this.focused = false
			this.isSettingsOpen = false
			this.$emit('move-block-up', this.block)
		},
		removeBlock () {
			this.$emit('remove-block', this.block)
		},
		immediatelyRemoveBlock() {
			this.$emit('remove-block', this.block)
		},
		closeBtnAction () {
			this.isSettingsOpen = false;
			this.separatedPopover = false;
		},
		toggleSettings () {
			this.isSettingsOpen = !this.isSettingsOpen;

			if (this.isSettingsOpen) {
				this.focused = true
			} else {
				this.separatedPopover = false
			}

			this.$nextTick(() => {
				if (this.isSettingsOpen) {
					console.log("calisti")
					this.$refs.settings.$el.style.removeProperty('display')

					if (this.settingsPopper) {
						this.settingsPopper.update()
					} else {
						this.settingsPopper = this.createPopper()
					}
					
				}
			})
		},
		createPopper() {
			const {
				settingsButton,
				settings
			} = this.$refs

			const self = this

			return Popper.createPopper(
				settingsButton.$el,
				settings.$el,
				{
					placement: "right-start",
					modifiers: [
						{
							name: "offset",
							options: { offset: [0, 10] }
						},
						{
							name: "computeStyles",
							options: {
								adaptive: false
							}
						},
						{
							name: "applyStyles",
							fn () {
								if (self.separatedPopover) return;
								const applyStyles = Popper.defaultModifiers.find(mod => mod.name == "applyStyles")
								applyStyles.fn(...arguments)
							}
						}
					]
				}
			)
		},
		mousedownHandler (event) {
			this.startDragPos.x = event.pageX;
			this.startDragPos.y = event.pageY;
			window.addEventListener("mousemove", this.mousemoveHandler);
			window.addEventListener("mouseup", this.mouseupHandler);
			this.separatedPopover = true;

			this.$nextTick(() => {
				const mat = new DOMMatrix(this.$refs.settings.$el.style.transform);
				this.popperPos.x = mat.e;
				this.popperPos.y = mat.f;
			})
		},
		mousemoveHandler (event) {
			const 	x = this.popperPos.x + event.pageX - this.startDragPos.x,
					y = this.popperPos.y + event.pageY - this.startDragPos.y;

			this.$refs.settings.$el.style.transform = `translate(${x}px, ${y}px)`
		},
		mouseupHandler () {
			window.removeEventListener("mousemove", this.mousemoveHandler);
			window.removeEventListener("mouseup", this.mouseupHandler);
			const mat = new DOMMatrix(this.$refs.settings.style.transform);
			this.popperPos.x = mat.e;
			this.popperPos.y = mat.f;
		},
		isObject (obj) {
			return Util.isObject(obj)
		},
		renderHTML () {
			this.$emit('render-html', this.block.toHTML())
		},
		capitalized (str) {
			if (typeof str !== 'string') return ''
			return str.charAt(0).toUpperCase() + str.slice(1)
		},
		nextEditableInView (editable) {
			const editables = this.$refs.view.$children.filter(comp => comp.$options.name == "Editable")
			const index = editables.indexOf(editable)
			return editables[index+1]
		},
		showControlButtons () {
			this.$refs.controlButtons.style.removeProperty("display");
			this.$refs.controlButtons.style.removeProperty("transform");
			Object.assign(this.$refs.controlButtons.style, {
				width: this.$el.offsetWidth+'px'
			})
			const diff = Util.matchTransformMatrix({
				a: this.$refs.controlButtons,
				b: this.$el
			})
			const transform = Util.transformStyle({ x: diff.diffX, y: diff.diffY })
			Object.assign(this.$refs.controlButtons.style, { transform })
		},
		clickOutsideHandler (e) {
			// e.path isn't valid for Firefox. 
			const path = e.path || (e.composedPath && e.composedPath());

			if (path.includes(this.$refs.controlButtons) 
				|| path.includes(this.$refs.settings.$el)) return;

			this.focused = false
			this.isSettingsOpen = false
			this.hideBackground()
		},
		focusHandler () {
			this.focused = true
		},
		showBackground () {
			if (this.backgroundVisible) return
			this.backgroundVisible = true

			this.$nextTick(() => {
				const blockRect = this.$el.getBoundingClientRect();

				this.$refs.background.style.removeProperty("display");
				this.$refs.background.style.removeProperty("transform");
				const bgRect = this.$refs.background.getBoundingClientRect();
	
				const translateX = blockRect.x - bgRect.x - 10
				const translateY = blockRect.y - bgRect.y - 10
				
				const transform = `translate(${translateX}px,${translateY}px)`;

				const width = blockRect.width + 20
				const height = blockRect.height + 20
	
				this.backgroundStyle = {
					transform,
					width: `${width}px`,
					height: `${height}px`
				}
			})
		},
		hideBackground () {
			this.backgroundVisible = false
			this.backgroundStyle = {}
		}
	},
}
</script>

<style scoped>
.block-background {
	position: absolute;
	z-index: 0;
	background: #f7ffff;
}

.block-background.focused {
	background: azure;
}

.block-background.unknown-block {
	background-color: #fbeded;
}

.control {
	position: absolute;
	display: flex;
	justify-content: flex-end;
	z-index: 100;
	pointer-events: none;
}

.control > * {
	pointer-events: all;
}

.option-label {
	display: block;
	letter-spacing: 1px;
	font-size: .8em;
	margin: 16px 0;
}
</style>